/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { DomainForm, VisitDetails } from "src/api";
import {
    ListingItem,
    Field,
    offerPlacement,
    Category,
    Image,
    TextBlock,
} from "@lib/shared/types";
import { BlogPostType } from "src/api/getBlog";
import { DomainInfo } from "src/api";
import { BACKEND_VALIDATION_ERRORS_MESSAGES } from "./shared/config";
import { Condition, HideDoNotSellCriteria, Rule } from "src/api/domainInfo";
import { RouteConfig } from "@page-builder/ts/types";

export const getSecondServiceCategoryIcon = (
    tag: string,
    category: Category,
    form?: DomainForm,
): Image | null => {
    const getSecondServiceCategory = (
        categoryName: string,
        category: Category,
    ) => {
        return category?.secondServiceCategories
            ?.map((el) => el.secondServiceCategory)
            .find((el) => el.name === categoryName);
    };
    let secondCategory;
    if (tag === "undefined") {
        secondCategory = form
            ? getSecondServiceCategory(form.categoryName as string, category)
            : getSecondServiceCategory(tag, category);
    } else {
        secondCategory = getSecondServiceCategory(tag, category);
    }

    if (secondCategory) {
        return secondCategory.icon;
    }
    return null;
};

export const doesOfferHaveALink = (offer: ListingItem): boolean => {
    if (offer?.placements?.hidePhoneNumber) {
        return false;
    }
    return !!(
        (!!offer.attributes?.phoneNumber ||
            !!offer.hiddenAttributes?.phoneNumber ||
            offer.proxiedNumber) &&
        (offer.link as string)?.startsWith("http")
    );
};

export const createHandlePreventPageClose = (
    isFormSubmitted: () => boolean,
) => {
    return (e: WindowEventMap["beforeunload"]): void => {
        // skip if form is submitted (either this instance or any other on the page)
        if (isFormSubmitted()) {
            return;
        }
        // Also skip if any form instance on the page has completed submission
        // (e.g. exit modal form submitted while parent form's ref is still false)
        if (
            typeof window !== "undefined" &&
            (window as unknown as Record<string, unknown>)
                .__anyFormSubmitted === true
        ) {
            return;
        }
        const href =
            document.activeElement?.attributes?.getNamedItem("href")?.nodeValue;

        if (href?.includes("tel:") || href?.startsWith("/")) {
            return;
        }
        e.preventDefault(); // fireFox
        e.returnValue = "";
    };
};

export const differenceBetweenTwoObjects = (
    origObj: {} | null,
    newObj: {} | null,
): unknown[] | Record<string, unknown> | null | undefined => {
    /**
     * Recursively finds differences between two objects
     * @param newObject - The new/updated object
     * @param original - The original object to compare against
     * @returns Object containing only the changed properties
     */
    function changes(
        newObject: unknown,
        original: unknown,
    ): unknown[] | Record<string, unknown> | null | undefined {
        // Handle null/undefined cases
        if (newObject === null || newObject === undefined) {
            return newObject !== original ? newObject : undefined;
        }

        // Handle primitive types and non-objects
        if (typeof newObject !== "object") {
            return newObject !== original ? newObject : undefined;
        }

        // Handle Date objects
        if (newObject instanceof Date && original instanceof Date) {
            return newObject.getTime() !== original.getTime()
                ? newObject
                : undefined;
        }

        // Handle arrays
        if (Array.isArray(newObject)) {
            if (!Array.isArray(original)) {
                return newObject as unknown[];
            }

            const changedItems: unknown[] = [];
            let hasChanges = false;

            for (let i = 0; i < newObject.length; i++) {
                const newItem: unknown = newObject[i];
                const originalItem: unknown = original[i];

                // Deep comparison for objects
                if (
                    typeof newItem === "object" &&
                    newItem !== null &&
                    typeof originalItem === "object" &&
                    originalItem !== null
                ) {
                    const nestedChanges = changes(newItem, originalItem);
                    if (
                        nestedChanges !== undefined &&
                        Object.keys(nestedChanges).length > 0
                    ) {
                        changedItems.push(nestedChanges);
                        hasChanges = true;
                    }
                } else if (newItem !== originalItem) {
                    changedItems.push(newItem);
                    hasChanges = true;
                }
            }

            // Return array only if there are changes
            return hasChanges || newObject.length !== original.length
                ? changedItems
                : undefined;
        }

        // Handle plain objects
        const result: Record<string, unknown> = {};
        let hasChanges = false;

        for (const key in newObject) {
            if (!Object.prototype.hasOwnProperty.call(newObject, key)) continue;

            const newValue = (newObject as Record<string, unknown>)[key];
            const originalValue = (original as Record<string, unknown>)?.[key];

            // Check if values are different
            if (
                typeof newValue === "object" &&
                newValue !== null &&
                typeof originalValue === "object" &&
                originalValue !== null
            ) {
                // Recursively check nested objects
                const nestedChanges = changes(newValue, originalValue);
                if (
                    nestedChanges !== undefined &&
                    (Array.isArray(nestedChanges) ||
                        Object.keys(nestedChanges).length > 0)
                ) {
                    result[key] = nestedChanges;
                    hasChanges = true;
                }
            } else if (newValue !== originalValue) {
                // Simple value change
                result[key] = newValue;
                hasChanges = true;
            }
        }

        return hasChanges ? result : {};
    }
    return changes(newObj ?? {}, origObj ?? {});
};

export const getUserMeta = () => {
    const ratio = window.devicePixelRatio || 1;
    const is_touch_device = "ontouchstart" in document.documentElement;
    const touchStatus = is_touch_device ? "touch" : "no-touch";
    const screenSize = `${screen.width}x${screen.height}`;
    const documentDimension = `${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`;
    const rw = screen.width * ratio;
    const rh = screen.height * ratio;
    const widthHeightRatio = `${rw}x${rh}`;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

    const connectionEffectiveType: string =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (connection?.effectiveType as string) ?? "";
    return {
        screenSize,
        documentDimension,
        ratio,
        widthHeightRatio,
        touchStatus,
        connection: connectionEffectiveType,
    };
};

export function hexToRGBA(hex: string, opacity: number): string {
    return (
        "rgba(" +
        (hex = hex.replace("#", ""))
            .match(new RegExp("(.{" + hex.length / 3 + "})", "g"))
            ?.map(function (l) {
                return parseInt(hex.length % 2 ? l + l : l, 16);
            })
            .concat(isFinite(opacity) ? opacity : 1)
            .join(",") +
        ")"
    );
}

export const arrayToKeyedObject = <T extends { [x: string]: string }>(
    arr: T[],
    key: string,
): { [x: string]: T } => {
    return arr.reduce<{ [x: string]: T }>((obj, item) => {
        obj[item[key]] = item;
        return obj;
    }, {});
};

export const gettingYears = (startYear: number, endYear: number): number[] => {
    return new Array(endYear - startYear + 1)
        .fill(0)
        .map((year, index) => endYear - index);
};

export const formatPhoneNumber = (phoneNumber: string | undefined) => {
    const cleaned = ("" + phoneNumber).replace(/\D/g, "");
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return ["(", match[2], ") ", match[3], "-", match[4]].join("");
    }
    return null;
};

export const getPhoneNumber = (
    offer: ListingItem | undefined,
): string | undefined => {
    if (offer?.placements?.hidePhoneNumber) {
        return "";
    }
    if (offer?.textBlocks?.position === "offer-associated-phone-number") {
        return offer?.textBlocks.text;
    }
    if (offer?.proxiedNumber && offer.proxiedNumber.phoneNumber) {
        return offer?.proxiedNumber.phoneNumber;
    }

    if (offer?.isPhoneNumber && offer.phoneLabel) {
        return offer.phoneLabel;
    }

    const hiddenAttributesNumber = offer?.hiddenAttributes?.phoneNumber;
    const attributesNumber = offer?.attributes.phoneNumber;

    return attributesNumber ?? hiddenAttributesNumber ?? "";
};

export const getHomepageHref = (
    query: { ts?: string | undefined },
    locale: string,
    defaultLocale: string | null,
): string | undefined => {
    if (query.ts !== undefined) return undefined;
    if (!defaultLocale || locale === defaultLocale) return "/";
    return `/${locale}`;
};

export const orderListByKeyword = (keyword: string, offers: ListingItem[]) => {
    if (!keyword || typeof keyword !== "string") {
        return offers;
    }
    if (offers && offers.length > 1) {
        const listItemMatched = offers?.find((item) => {
            if (item.slug.indexOf(keyword.toLocaleLowerCase()) > -1) {
                return item;
            }
        });
        if (listItemMatched) {
            return [
                listItemMatched,
                ...offers.filter((item) => item.slug !== listItemMatched.slug),
            ];
        }
    }
    return offers;
};

const streetAddressFields = ["streetAddress", "streetAddress2"];
export const isStreetAddress = (codeName: string): boolean => {
    return streetAddressFields.includes(codeName);
};
export const getZipCodeField = (fields: Field[]) => {
    return fields.find(
        (f: Field) => f.codeName === "zipCode" || f.codeName === "toZipCode",
    );
};

export const monthsArr = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

export const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) {
        return "";
    }
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
    };
    return new Intl.DateTimeFormat("en-US", options)?.format(date);
};

export const getDateMinusDays = (days?: number) => {
    const date = new Date();
    if (days) date.setDate(date.getDate() - days);
    return formatTimestamp(date.toISOString());
};

export const replacePlaceholders = (
    text: string,
    args?: {
        region?: string;
        category?: string;
        city?: string;
        genericRoute?: string;
        keyword?: string;
        title?: string;
        queryString?: string;
        callsCampaignTitle?: string;
        postUpdatedDate?: string;
        readingTime?: string;
        phoneNumber?: string;
    },
) => {
    return text
        ?.replace(/\[m\]/gi, monthsArr[new Date().getMonth()])
        ?.replace(/\[y\]/gi, String(new Date().getFullYear()))
        ?.replace(/\[d\]/gi, String(formatTimestamp(new Date().toISOString())))
        ?.replace(/\[d-(\d+)\]/gi, (_, days) =>
            String(getDateMinusDays(Number(days))),
        ) // the format is [d-number]
        ?.replace(/\[s(?::([^\]]+))?\]/gi, (_, fallback: string) => {
            return args?.region ?? fallback ?? "";
        }) // the format is [s: fallback] if region is not provided, it will use the fallback value, if fallback value is not provided it will be replaced by an empty string
        ?.replace(/\[c\]/gi, args?.category ?? "")
        ?.replace(/\[ci(?::([^\]]+))?\]/gi, (_, fallback: string) => {
            return args?.city ?? fallback ?? "";
        }) // the format is [ci: fallback] if city is not provided, it will use the fallback value, if fallback value is not provided it will be replaced by an empty string
        ?.replace(/\[t(?::([^\]]+))?\]/gi, (_, fallback: string) => {
            return args?.title || fallback || "";
        }) // the format is [t:fallback] if title is not provided, it will use the fallback value, if fallback value is not provided it will be replaced by an empty string
        ?.replace(/\[calls-campaign-title\]/gi, args?.callsCampaignTitle ?? "")
        ?.replace(/\[amp\]/gi, "&")
        ?.replace(/\[ds\]/gi, "$")
        ?.replace(/\[com\]/gi, ",")
        ?.replace(/\[sc\]/gi, ";")
        ?.replace(/\[co\]/gi, ":")
        ?.replace(/\[qt\]/gi, '"')
        ?.replace(/\[sqt\]/gi, "'")
        ?.replace(/\[p\]/gi, ".")
        ?.replace(/\[q\]/gi, "?")
        ?.replace(/\[per\]/gi, "%")
        ?.replace(/\[ex\]/gi, "!")
        ?.replace(/\[br\]/gi, "<br>")
        ?.replace(/\[da\]/gi, "&#45;")
        ?.replace(/\[sl\]/gi, "/")
        ?.replace(/\[k\]/gi, args?.keyword ?? "")
        ?.replace(/\[gr\]/gi, args?.genericRoute ?? "")
        ?.replace(/\[qs\]/gi, args?.queryString ?? "")
        ?.replace(/\[postUpdatedDate\]/gi, args?.postUpdatedDate ?? "")
        ?.replace(/\[readingTime\]/gi, args?.readingTime ?? "")
        ?.replace(/\[phoneNumber\]/gi, args?.phoneNumber ?? "");
};

// Decode HTML entities for plain-text render
// sites like <title> and meta tags where React escapes the raw entity.
export const decodeHtmlEntities = (text?: string | null): string => {
    if (!text) return "";
    const namedEntities: Record<string, string> = {
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&apos;": "'",
        "&nbsp;": " ",
    };
    return text
        .replace(/&#x([0-9a-f]+);/gi, (match, hex: string) => {
            const codePoint = parseInt(hex, 16);
            return codePoint <= 0x10ffff
                ? String.fromCodePoint(codePoint)
                : match;
        })
        .replace(/&#(\d+);/g, (match, dec: string) => {
            const codePoint = parseInt(dec, 10);
            return codePoint <= 0x10ffff
                ? String.fromCodePoint(codePoint)
                : match;
        })
        .replace(
            /&(amp|lt|gt|quot|apos|nbsp);/gi,
            (match) => namedEntities[match.toLowerCase()] ?? match,
        );
};

export const getMainTitlesNavigationFromBody = (html: string) => {
    const regex = /<[^>]+id="([^"]+)"[^>]*>([\s\S]+?)<\/[^>]+>/g;
    const result = [];

    let match;
    while ((match = regex.exec(html))) {
        const [, id, content] = match;

        const textNodes = content.match(/>([^<]+)/g);

        if (textNodes) {
            result.push(
                // eslint-disable-next-line no-unsafe-optional-chaining
                ...textNodes?.map((node) => ({
                    value: node.substring(1).trim(),
                    id,
                })),
            );
        } else {
            result.push({
                value: content.trim(),
                id,
            });
        }
    }

    return result
        .filter((i) => i.value?.length > 1)
        .map((el) => ({
            title: /^\d/.test(el.value)
                ? el.value.replace(/^\d/, "").slice(1)
                : el.value,
            link: `#${el.id}`,
        }));
};

export const extractExcerptFromBody = (body: string, characters: number) => {
    return body
        .replace(/<[^>]+>/g, "")
        .substring(0, characters)
        .trim();
};

export const getDescriptionForBlogPost = (blogPost: BlogPostType) => {
    if (blogPost?.model.excerpt) {
        return extractExcerptFromBody(blogPost?.model.excerpt, 155);
    }
    if (blogPost?.model.body) {
        return extractExcerptFromBody(blogPost?.model.body, 155);
    }
};

const getOfferPlacement = (offer: ListingItem) => {
    if (offer?.hiddenAttributes?.offerPlacement) {
        return JSON.parse(
            offer?.hiddenAttributes?.offerPlacement,
        ) as offerPlacement;
    } else {
        return null;
    }
};

export const getOffersByPlacement = (
    offers: ListingItem[],
    placement:
        | "thankYou"
        | "header"
        | "exitModal"
        | "offersList"
        | "showStickyOffer",
) => {
    if (placement === "thankYou") {
        return offers?.filter((offer) => offer?.placements?.showOnThankYou);
    }
    if (placement === "header") {
        return offers?.filter(
            (offer) => !offer?.placements?.hideNumberOnHeader,
        );
    }
    if (placement === "exitModal") {
        return offers?.filter(
            (offer) => offer?.placements?.displayOnExitWindow,
        );
    }
    if (placement === "offersList") {
        const finalOffersList = offers?.filter((offer) => {
            const offerPlacement = getOfferPlacement(offer);
            if (offerPlacement) {
                return (
                    offer?.features.length &&
                    (offerPlacement?.offersList === undefined
                        ? true
                        : offerPlacement?.offersList)
                );
            }
            return offer?.features.length;
        });
        return finalOffersList;
    }
    if (placement === "showStickyOffer") {
        const stickyOffers = offers?.filter((offer) => {
            if (
                getPhoneNumber(offer) &&
                !offer?.placements?.hideNumberOnHeader
            ) {
                return offer;
            }
        });
        return stickyOffers;
    }
};

export const finalShowForm = (
    showList: string | undefined,
    showForm: boolean,
    offers: ListingItem[],
) => {
    if (offers.length === 0) {
        return true;
    }

    const offersList = getOffersByPlacement(offers, "offersList");
    if (offersList?.length === 0) {
        return true;
    }

    if (showList && showList === "t") {
        return false;
    }
    if (showList && showList === "f") {
        return true;
    }
    return showForm;
};

export function lightenColor(
    color: string | undefined,
    factor: number,
): string {
    if (!color) {
        return "";
    }
    function clamp(value: number, min: number, max: number) {
        return Math.min(Math.max(value, min), max);
    }

    function rgbToHex(r: number, g: number, b: number) {
        return `#${((1 << 24) | (r << 16) | (g << 8) | b)
            .toString(16)
            .slice(1)}`;
    }

    function hexToRgb(hex: string) {
        hex = hex.replace(/^#/, "");
        return {
            r: parseInt(hex.substr(0, 2), 16),
            g: parseInt(hex.substr(2, 2), 16),
            b: parseInt(hex.substr(4, 2), 16),
        };
    }

    function hslToRgb(h: number, s: number, l: number) {
        let r, g, b;

        if (s === 0) {
            r = g = b = l;
        } else {
            const hueToRgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;

            r = Math.round(hueToRgb(p, q, h + 1 / 3) * 255);
            g = Math.round(hueToRgb(p, q, h) * 255);
            b = Math.round(hueToRgb(p, q, h - 1 / 3) * 255);
        }

        return { r, g, b };
    }

    function rgbToHsl(r: number, g: number, b: number) {
        (r /= 255), (g /= 255), (b /= 255);
        const max = Math.max(r, g, b),
            min = Math.min(r, g, b);
        let h,
            s,
            // eslint-disable-next-line prefer-const
            l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            //@ts-ignore
            h /= 6;
        }

        return { h, s, l };
    }

    if (color.startsWith("#")) {
        const rgb = hexToRgb(color);
        const newR = clamp(rgb.r + 255 * factor, 0, 255);
        const newG = clamp(rgb.g + 255 * factor, 0, 255);
        const newB = clamp(rgb.b + 255 * factor, 0, 255);
        return rgbToHex(newR, newG, newB);
    } else if (color.startsWith("rgb")) {
        const matches = color.match(/\d+/g);
        if (!matches || matches.length !== 3) {
            throw new Error("Invalid RGB color format");
        }
        const r = parseInt(matches[0], 10);
        const g = parseInt(matches[1], 10);
        const b = parseInt(matches[2], 10);
        const newR = clamp(r + 255 * factor, 0, 255);
        const newG = clamp(g + 255 * factor, 0, 255);
        const newB = clamp(b + 255 * factor, 0, 255);
        return `rgb(${newR}, ${newG}, ${newB})`;
    } else if (color.startsWith("hsl")) {
        const matches = color.match(/[\d.]+/g);
        if (!matches || matches.length !== 3) {
            throw new Error("Invalid HSL color format");
        }
        const h = parseFloat(matches[0]) / 360;
        const s = parseFloat(matches[1]) / 100;
        const l = parseFloat(matches[2]) / 100;
        const newHsl = hslToRgb(h, s, l);
        const newR = Math.round(clamp(newHsl.r + 255 * factor, 0, 255));
        const newG = Math.round(clamp(newHsl.g + 255 * factor, 0, 255));
        const newB = Math.round(clamp(newHsl.b + 255 * factor, 0, 255));
        const newHslColor = rgbToHsl(newR, newG, newB);
        //@ts-ignore
        const newHue = Math.round(newHslColor.h * 360);
        const newSaturation = Math.round(newHslColor.s * 100);
        const newLightness = Math.round(newHslColor.l * 100);
        return `hsl(${newHue}, ${newSaturation}%, ${newLightness}%)`;
    } else {
        return "#eee";
    }
}

export const parseToDollars = (val: string) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(parseInt(val, 10));
interface EvaluationContext {
    visitDetails?: VisitDetails;
    trafficSource: string | null;
    pageType: string;
}

const getNestedValue = (obj: any, path: string): unknown => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
    return path.split(".").reduce((current, key) => current?.[key], obj);
};

const evaluateCondition = (
    condition: Condition,
    context: EvaluationContext,
): boolean => {
    const { lookupCondition, operation, value } = condition;
    const actualValue = getNestedValue(context, lookupCondition) as string;

    switch (operation) {
        case "includes": {
            if (!value) return false;
            const includeValues = value.split(",").map((v) => v.trim());
            return includeValues.includes(actualValue);
        }
        case "not_include": {
            if (!value) return true;
            const excludeValues = value.split(",").map((v) => v.trim());
            return !excludeValues.includes(actualValue);
        }
        case "exist":
            return (
                actualValue !== null &&
                actualValue !== undefined &&
                actualValue !== ""
            );
        case "does_not_exist":
            return (
                actualValue === null ||
                actualValue === undefined ||
                actualValue === ""
            );
        default:
            return false;
    }
};

const evaluateRule = (rule: Rule, context: EvaluationContext): boolean => {
    const { operator, conditions } = rule;
    const results = conditions.map((condition) =>
        evaluateCondition(condition, context),
    );

    return operator === "and"
        ? results.every((r) => r)
        : results.some((r) => r);
};

const evaluateCriteria = (
    criteria: HideDoNotSellCriteria,
    context: EvaluationContext,
): boolean => {
    const { operator, rules } = criteria;
    const results = rules?.map((rule) => evaluateRule(rule, context));

    return operator === "and"
        ? results?.every((r) => r)
        : results?.some((r) => r);
};

export const injectDoNotSellInFooter = ({
    footerArray,
    trafficSource,
    pageType,
    hideDoNotSell,
    domain,
    domainNameTranslation,
    locale,
    defaultLocale,
    visitDetails,
    routeConfig,
}: {
    footerArray: {}[];
    trafficSource: string | null;
    pageType: string;
    hideDoNotSell: boolean | undefined;
    domain?: DomainInfo;
    domainNameTranslation?: string;
    locale?: string;
    defaultLocale?: string;
    visitDetails?: VisitDetails;
    routeConfig?: RouteConfig;
}) => {
    // Keep the original condition as is
    if (!trafficSource && pageType !== "do-not-sell" && !hideDoNotSell) {
        const hideDoNotSellInFooter = domain?.config?.hideDoNotSellInFooter;
        // Evaluate additional criteria if provided
        if (hideDoNotSellInFooter) {
            const context = { visitDetails, trafficSource, pageType };
            const shouldHide = evaluateCriteria(hideDoNotSellInFooter, context);
            if (shouldHide) {
                return; // Don't inject the link
            }
        }

        //hide do not sell link using page settings
        if (routeConfig?.config?.settings?.hideDoNotSell) {
            return;
        }
        const translatedDoNotSell =
            domain?.translations && domainNameTranslation
                ? domain?.translations?.[`${domainNameTranslation}_DoNotSell`]
                : null;
        footerArray.push({
            title: translatedDoNotSell ?? "Do Not Sell",
            onClick: () => {
                if (locale && defaultLocale) {
                    window.location.href = `${
                        locale === (defaultLocale ?? "en") ? "" : `/${locale}`
                    }/do-not-sell`;
                } else {
                    window.location.href = `/do-not-sell`;
                }
            },
        });
    }
};

export const slugToTitle = (slug?: string) => {
    if (!slug) return "";
    const words = slug.split("-");

    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        words[i] = word.charAt(0).toUpperCase() + word.slice(1);
    }

    return words.join(" ");
};

export const extractContentFromHtml = (s: string | undefined): string => {
    if (s) {
        const span = document.createElement("span");
        span.innerHTML = s;
        return span.textContent || span.innerText;
    }
    return s || "";
};

export const shouldRemoveBuyer = (
    eb: string | string[] | undefined,
    offer: ListingItem,
): boolean => {
    if (eb && offer?.buyer?.id) {
        return eb.includes(offer?.buyer?.id + "");
    }
    return false;
};

export const commonPartnerWithUsAllowedCategories = ["auto-accident"];

export const SKIPPED_OPERATION_ID_KEY = "skipped-operation-id";

export const getTitle = ({
    formattedTitle,
    textBlocks,
    category,
    visitDetails,
    ignoreTextBlocks,
    isFormInsideOffersModal,
    overrideStepTitle,
    forceStepTitle,
    stepTitle,
    customMainTitle,
}: {
    formattedTitle?: string | null;
    textBlocks?: TextBlock[] | null;
    category?: Category | null;
    visitDetails: { region?: string; city?: string };
    ignoreTextBlocks?: boolean;
    isFormInsideOffersModal?: boolean;
    overrideStepTitle?: string;
    forceStepTitle?: boolean;
    stepTitle?: string;
    customMainTitle?: string;
}) => {
    if (formattedTitle && formattedTitle.length) {
        return formattedTitle;
    } else if (
        (!ignoreTextBlocks &&
            textBlocks &&
            textBlocks.length &&
            textBlocks.find((item) => item.position === "form_title")) ||
        (isFormInsideOffersModal &&
            textBlocks?.find(
                (item) =>
                    item.position === "form-inside-exit-modal-first-step-title",
            ))
    ) {
        if (isFormInsideOffersModal) {
            return replacePlaceholders(
                textBlocks.find(
                    (item) =>
                        item.position ===
                        "form-inside-exit-modal-first-step-title",
                )?.text as string,
                {
                    category: category?.name,
                    region: visitDetails.region,
                    city: visitDetails?.city,
                },
            );
        }
        return replacePlaceholders(
            textBlocks.find((item) => item.position === "form_title")
                ?.text as string,
            {
                category: category?.name,
                region: visitDetails.region,
                city: visitDetails?.city,
            },
        );
    } else {
        return overrideStepTitle
            ? overrideStepTitle
            : forceStepTitle
            ? stepTitle
            : customMainTitle
            ? customMainTitle
            : stepTitle;
    }
};

export const stripHtmlTags = (input: string) => {
    return input.replace(/<\/?[^>]+(>|$)/g, "");
};

export const replaceStringVariables = (
    template: string,
    variables: Record<string, string>,
): string => {
    return template.replace(/\$\{([^}]+)\}/g, (_, key: string) => {
        return variables[key] ?? "";
    });
};

export function base64EncodeUnicode(str: string): string {
    const utf8Bytes = new TextEncoder().encode(str);
    const binary = Array.from(utf8Bytes)
        .map((byte) => String.fromCharCode(byte))
        .join("");
    // Convert to URL-safe base64 by replacing +, /, and = with URL-safe characters
    return btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");
}

export function base64DecodeUnicode(base64: string): string {
    try {
        // Convert URL-safe base64 back to standard base64
        let standardBase64 = base64.replace(/-/g, "+").replace(/_/g, "/");

        // Add padding if necessary
        while (standardBase64.length % 4) {
            standardBase64 += "=";
        }

        const binary = atob(standardBase64);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to decode base64 string:", error);
        // Return empty string or throw error based on your needs
        return "{}";
    }
}

export const getDomainNameWithoutExtension = (domainName: string) => {
    return domainName?.split(".")?.[0]?.toLowerCase();
};

export const getErrorMessageTranslation = (
    domain: DomainInfo,
    errorCode: string,
    locale: string,
    fallBackMessage?: string,
): string => {
    const backendMessages = BACKEND_VALIDATION_ERRORS_MESSAGES as Record<
        string,
        Record<string, string>
    >;
    return (
        domain?.translations?.[errorCode] ??
        backendMessages[errorCode]?.[locale ?? "en"] ??
        fallBackMessage ??
        "Something went wrong"
    );
};

// if the textBlock has [phoneNumber] its replaced by a clickable link, else stays the same
export const renderQrText = (
    qrTextBlock: string | undefined,
    phoneNumber: string | undefined,
): string => {
    if (!qrTextBlock || !phoneNumber) return "";

    const formattedPhone = formatPhoneNumber(phoneNumber);

    return qrTextBlock.replace(
        "[phoneNumber]",
        `<a class="phoneLink" href="tel:${phoneNumber}" data-phone="${phoneNumber}">
                ${formattedPhone as string}
            </a>`,
    );
};
